import { Box, Card, Stack, Typography, useMediaQuery } from '@mui/material'
import type { MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { buildProjectCommentNotificationUrl } from '../../../utils/commentNotificationLink'
import { PureLightTheme } from '../../../styles/theme'
import type {
  NotificationActorPayload,
  ProjectCommentNotificationPayload,
  ProjectCommentNotificationResource,
} from '../../../api/notifications'
import type { IProjectAuthorShort } from '../../../api/types/projects'
import {
  mediaSourceUrl,
  notificationActorAvatarSrc,
  notificationActorDisplayName,
  notificationActorPathKey,
  resolveNotificationActor,
} from './utils'
import { UserAvatar } from '../../common/UserAvatar'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { IconProjectPlaceholder } from '../../../icons/IconProjectPlaceholder'
import { StackedAvatars } from '../../ui-kit'
import { createNotificationNavHandlers, getProjectSlug } from './notificationNav'
import { ActorNameSpan } from './notificationTypography'

type Props = {
  notification: ProjectCommentNotificationResource
}

function headerSubtitle(type: ProjectCommentNotificationResource['type']): string {
  switch (type) {
    case 'project.comment.created':
      return 'Новый комментарий'
    case 'project.comment.replied':
      return 'Ответ в обсуждении'
    case 'project.comment.liked':
      return 'Лайк комментария'
    default:
      return 'Комментарий'
  }
}

function actionLineWithActorLink(
  type: ProjectCommentNotificationResource['type'],
  actorName: string,
  onUserClick: (e: MouseEvent, pathKey: string) => void,
  actorPathKey: string,
) {
  const suffix =
    type === 'project.comment.created'
      ? ' оставил комментарий в обсуждении'
      : type === 'project.comment.replied'
        ? ' ответил вам в обсуждении'
        : type === 'project.comment.liked'
          ? ' поставил лайк вашему комментарию'
          : ' — действие в обсуждении'

  return (
    <Typography variant="black-14-normal" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
      <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
        {actorName}
      </ActorNameSpan>
      {suffix}
    </Typography>
  )
}

function likeStackUsers(
  project: ProjectCommentNotificationPayload['project'],
  projectTitle: string,
  projectAvatar: string | undefined,
  actor: NotificationActorPayload,
  actorName: string,
  actorAvatar: string | undefined,
): IProjectAuthorShort[] {
  const pid = project && typeof project.id === 'number' ? project.id : null
  const aid = typeof actor.id === 'number' && actor.id > 0 ? actor.id : null
  return [
    {
      id: pid,
      slug: project && typeof project.slug === 'string' ? project.slug : null,
      fullname: projectTitle,
      avatar: projectAvatar ?? null,
    },
    {
      id: aid,
      slug: actor.slug ?? null,
      fullname: actorName,
      avatar: actorAvatar ?? null,
      deleted: actor.deleted,
    },
  ]
}

export function ProjectCommentNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onProjectClick, onUserClick } = createNotificationNavHandlers(navigate)
  const isMobile = useMediaQuery(PureLightTheme.breakpoints.down('md'))
  const payload = notification.payload
  const project = payload.project ?? null
  const actor = resolveNotificationActor(payload, notification.actor_user_id)
  const projectTitle =
    project && typeof project.title === 'string' && project.title.trim().length > 0 ? project.title : 'Проект'
  const actorName = notificationActorDisplayName(actor)
  const projectAvatarSrc = mediaSourceUrl(project?.image_url) ?? undefined
  const actorAvatarSrc = notificationActorAvatarSrc(actor)
  const preview =
    typeof payload.preview === 'string' && payload.preview.trim().length > 0 ? payload.preview.trim() : null

  const projectSlug = getProjectSlug(
    project,
    notification.subject_kind === 'project' ? notification.subject_id : null,
  )
  const actorPathKey = notificationActorPathKey(actor, notification.actor_user_id)

  const navigateToComment = () => {
    if (!projectSlug) {
      toast.error('Проект недоступен')
      return
    }
    navigate(buildProjectCommentNotificationUrl(projectSlug, payload, { isMobile }))
  }

  const previewSx = { opacity: 0.9, wordBreak: 'break-word' as const }

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2, cursor: 'pointer' }} onClick={navigateToComment}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onProjectClick(e, projectSlug)}
          sx={{ cursor: projectSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={projectTitle}
            avatarSrc={projectAvatarSrc}
            fallbackIcon={<IconProjectPlaceholder fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle={headerSubtitle(notification.type)}
          />
        </Box>

        {notification.type === 'project.comment.liked' ? (
          <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
            <StackedAvatars
              users={likeStackUsers(project, projectTitle, projectAvatarSrc, actor, actorName, actorAvatarSrc)}
              size={34}
              overlap={8}
            />
            {actionLineWithActorLink(notification.type, actorName, onUserClick, actorPathKey)}
          </Stack>
        ) : (
          <Stack direction="row" gap={1.5} alignItems="flex-start" minWidth={0}>
            <Box
              onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
              sx={{ cursor: actorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
            >
              <UserAvatar src={actorAvatarSrc ?? null} size={34} />
            </Box>
            <Stack minWidth={0} gap={0.5} flex={1}>
              {actionLineWithActorLink(notification.type, actorName, onUserClick, actorPathKey)}
              {preview && (
                <Typography variant="black-12-light" sx={previewSx}>
                  «{preview}»
                </Typography>
              )}
            </Stack>
          </Stack>
        )}

        {notification.type === 'project.comment.liked' && preview && (
          <Typography variant="black-12-light" sx={previewSx}>
            «{preview}»
          </Typography>
        )}

        {typeof notification.message === 'string' && notification.message.trim().length > 0 && (
          <Typography variant="black-12-light" sx={{ opacity: 0.85 }}>
            {notification.message}
          </Typography>
        )}

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
